// Global
import { sendGTMEvent } from '@next/third-parties/google';
import { Item, Placeholder, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useRef, useState } from 'react';
import graphQLClientFactory from 'lib/graphql-client-factory';

// Lib
import { ComponentProps } from 'lib/component-props';
import { CompositeComponents, Ingredients } from 'lib/templates/Feature.Dart.model';

// Local
import Container from 'components/authorable/Layout/DartContainer/DartContainer';
import GoogleMaterialSymbol from 'helpers/GoogleMaterialSymbol/GoogleMaterialSymbol';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import Video from 'helpers/Video/Video';
import { stripHtml } from 'lib/utils/regex';
import LegalDisclaimer from 'helpers/LegalDisclaimer/LegalDisclaimer';
import Button from 'helpers/Button/Button';
import { GetStaticComponentProps } from '@sitecore-jss/sitecore-jss-nextjs';
import { IngredientQueryService } from 'lib/ingredient-service';
import IngredientQuery from '../GlossaryTab/Ingredient.graphql';
import { useTheme } from 'lib/context/ThemeContext';
import { tailwindVariants } from './AccordionTailwind';
import Link from 'next/link';

type Alignment = 'left' | 'right' | undefined;

export type AccordionListProps = ComponentProps &
  CompositeComponents.Accordion.AccordionList & { staticProps?: RawGqlData[] } & {
    ingredientsURL?: string;
  };
export type AccordionList = CompositeComponents.Accordion.Accordion;

export type IngredientsDataType = Ingredients.Ingredient &
  Ingredients.Allergen &
  Ingredients.Fragrance;

export type IngredientIndex = Ingredients.IngredientIndex;

export type RawGqlData = {
  ingredients: IngredientData[];
};

export type IngredientData = {
  name: string;
  title: {
    value: string;
  };
  definition: {
    value: string;
  };
  active: {
    value: string;
  };
};

export type RawStartItemGqlData = {
  layout: {
    item: {
      id: string;
      path: string;
    };
  };
};
export type Path = {
  parent: {
    url: {
      path: string;
    };
  };
};
export type RawPathGqlData = {
  search: {
    results: Path[];
  };
};

const Accordion = (props: AccordionListProps): JSX.Element => {
  const { themeName } = useTheme();

  /**
   * Check the configuration for the Accordion
   * Read Rendering parameter for option
   * 1. Accordion Can open at a time
   * 2. One Accordion Open at a time
   * 3. Multiple Accordion can open at the same Time
   */

  const { accordions, description, title, disclaimerText, CTA } = props?.fields ?? {};
  const ingredientLength = props?.staticProps || '';
  const ingredientsPath = props?.ingredientsURL;

  const {
    enableRTL: alignmentParam,
    expandedFirstAccordionOnPageLoad: expandedFirstAccordionOnPageLoadParam,
    openMultipleAccordionAtSameTime,
    useIngredientLinks,
  } = props?.params ?? {};

  // Check Rendering Parameter for Accordion Configuration
  const alignment = alignmentParam === '1' ? 'right' : 'left';
  const expandedFirstAccordionOnPageLoad = expandedFirstAccordionOnPageLoadParam ? 0 : -1;

  const {
    base,
    componentHeaderContentWrapper,
    componentDescription,
    componentTitle,
    pane,
    paneBody,
    paneHeader,
    paneBodyMediaWrapper,
    paneTitle,
    paneTitleButton,
    paneTitleIcon,
    paneTitleText,
    paneWrapper,
    videoClasses,
    wrapper,
    backgroundBG,
    disclaimerContentClasses,
    disclaimerClasses,
    themeBasedContainerClass,
    glossaryTabContainer,
    ingredientContainer,
    accordionTabContentHeader,
    ingredientTitle,
    ingredientLinks,
  } = tailwindVariants({
    alignment: alignment as Alignment,
    ingredientLength: (ingredientLength?.length > 0) as boolean,
    /* eslint-disable  @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    brand: themeName as string,
    useIngredientLinks: useIngredientLinks !== '1' ? true : false,
  });

  // Set State
  const [activeIndexes, setActiveIndexes] = useState<number[]>([expandedFirstAccordionOnPageLoad]);

  /**
   * handleAccordionClick function is to handle Accordion.
   * 1. It can open one at a time
   * 2. It can open multiple at a time
   * @param index : to manage accordion item
   * @param openMultipleAccordionAtSameTime : pass accordion configuration
   */
  const accordionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleAccordionClick = (
    index: number,
    openMultipleAccordionAtSameTime: string | undefined,
    id: string | undefined,
    name: string | undefined
  ) => {
    // Set Indexes for Accordion
    if (openMultipleAccordionAtSameTime) {
      setActiveIndexes([...activeIndexes, index]);
    } else {
      setActiveIndexes([index]);
    }

    // Filter indexes for Accordion
    if (activeIndexes.includes(index)) {
      sendGTMEvent({
        event: 'accordion',
        type: 'close',
        'gtm.element.dataset.gtmDatasourceId': id,
        'gtm.element.dataset.gtmComponentName': name,
      });
      setActiveIndexes(activeIndexes.filter((item) => item !== index));
    } else {
      sendGTMEvent({
        event: 'accordion',
        type: 'open',
        'gtm.element.dataset.gtmDatasourceId': id,
        'gtm.element.dataset.gtmComponentName': name,
      });

      // Scroll to the clicked accordion tab
      const currentAccordion = accordionRefs.current[index];
      if (currentAccordion) {
        setTimeout(() => {
          const elementTop = currentAccordion.getBoundingClientRect().top + window.scrollY;

          // Prefer jumplinks height if present, else use header height
          const jumplinks = document.querySelector('#jump-links') as HTMLElement | null;
          const header = document.querySelector('header') as HTMLElement | null;

          const offset = jumplinks?.offsetHeight || header?.offsetHeight || 0;
          const newPosition = elementTop - offset;

          window.scrollTo({ top: newPosition, behavior: 'smooth' });
        }, 300);
      }
    }
  };

  const id = props?.params?.RenderingIdentifier;

  return (
    <div
      className={backgroundBG()}
      data-component="authorable/accordion"
      id={id ? id : undefined}
      tabIndex={-1}
    >
      <Container className={themeBasedContainerClass()}>
        <div
          data-component="authorable/general/accordion"
          data-testid="accordion"
          className={base()}
        >
          <div className={wrapper()}>
            <div className={paneWrapper()}>
              {(title?.value || description?.value) && (
                <div className={paneHeader()}>
                  <div className={componentHeaderContentWrapper()}>
                    {title?.value && (
                      <div className={componentTitle()}>
                        <Text encode={false} field={title} tag="h2" />
                      </div>
                    )}
                    {description?.value && (
                      <div className={componentDescription()}>
                        <Text encode={false} field={description} tag="p" />
                      </div>
                    )}
                  </div>
                  {CTA?.value?.text &&
                    CTA?.value?.href &&
                    ((themeName as string) === 'Pyrel' ? (
                      <Button
                        label={CTA.value.text}
                        title={CTA?.value?.title}
                        type="outline"
                        color="tonal"
                        tag="a"
                        href={CTA.value.href}
                        linktype={CTA?.value?.linktype}
                        target={CTA?.value?.target}
                      />
                    ) : (
                      <Button
                        label={CTA.value.text}
                        title={CTA?.value?.title}
                        type="outline"
                        tag="a"
                        href={CTA.value.href}
                        linktype={CTA?.value?.linktype}
                        target={CTA?.value?.target}
                      />
                    ))}
                </div>
              )}
              {accordions &&
                accordions?.length &&
                accordions?.map((accordion: AccordionList, index: number) => {
                  const { paneBodyWrapper } = tailwindVariants({
                    isOpen: activeIndexes.includes(index) as boolean,
                  });

                  const { bottomDescription, topDescription, image, video, usePlaceholder } =
                    accordion?.fields ?? {};
                  const { id, name } = (accordion as Item) || {};
                  /* eslint-disable  @typescript-eslint/no-explicit-any */
                  const { useIngredients } = accordion?.fields as any;

                  const phKey = `dartaccordionrow-${index}-{*}`;
                  return (
                    <React.Fragment key={index}>
                      {/* TODO: components-accordion-spacing-large-item-item-radius is missing */}
                      <div
                        ref={(el) => {
                          accordionRefs.current[index] = el;
                        }}
                        className={pane()}
                        data-id={index}
                        key={index}
                      >
                        <div className={paneTitle()}>
                          <button
                            aria-label={stripHtml(
                              accordion?.fields?.accordionLabel?.value as string
                            )}
                            type="button"
                            className={paneTitleButton()}
                            onClick={() =>
                              handleAccordionClick(index, openMultipleAccordionAtSameTime, id, name)
                            }
                            aria-expanded={activeIndexes.includes(index)}
                          >
                            <div className={paneTitleText()}>
                              <RichTextA11yWrapper field={accordion?.fields?.accordionLabel} />
                            </div>
                            <GoogleMaterialSymbol
                              className={paneTitleIcon()}
                              icon={activeIndexes.includes(index) ? 'expand_less' : 'expand_more'}
                            />
                          </button>
                        </div>
                        <div
                          id={`accordion-panel-${index}-${id}`}
                          aria-labelledby={`accordion-panel-${index}-${id}-label`}
                          aria-expanded={activeIndexes.includes(index)}
                          className={paneBodyWrapper()}
                        >
                          {/* For Glossary Component, added this condition when we have Ingredients available then render Ingredients else render usePlaceholder content used in general Accordion Content. */}
                          {useIngredients?.value ? (
                            <div className={glossaryTabContainer()}>
                              {Array.isArray(accordion?.fields?.ingredients) &&
                                !!accordion?.fields?.ingredients?.length &&
                                accordion?.fields?.ingredients?.map(
                                  (ingredientData: IngredientIndex) => {
                                    const ingredientIndexTitle = ingredientData?.fields?.title
                                      ?.value as string;
                                    return (
                                      <>
                                        <div key={index} className="ingredientWrapper">
                                          {Array.isArray(props?.staticProps) &&
                                            !!props?.staticProps?.length &&
                                            props?.staticProps?.map(
                                              (item: RawGqlData, itemIndex: number) => {
                                                const filteredIngredients =
                                                  item?.ingredients?.filter(
                                                    (ingredient: IngredientData) => {
                                                      const ingredientTitle =
                                                        ingredient?.title?.value;
                                                      if (!ingredientTitle || !ingredientIndexTitle)
                                                        return false;

                                                      // "0" means titles starting with numbers
                                                      if (ingredientIndexTitle === '0') {
                                                        return /^[0-9]/.test(ingredientTitle);
                                                      }

                                                      // "#" means titles starting with special characters
                                                      if (ingredientIndexTitle === '#') {
                                                        return /^[^a-zA-Z0-9]/.test(
                                                          ingredientTitle
                                                        );
                                                      }

                                                      // Otherwise, match starting alphabet
                                                      return ingredientTitle
                                                        .toLowerCase()
                                                        .startsWith(
                                                          ingredientIndexTitle.toLowerCase()
                                                        );
                                                    }
                                                  );

                                                // Skip rendering if there are no filtered ingredients
                                                if (
                                                  !filteredIngredients ||
                                                  filteredIngredients.length === 0
                                                )
                                                  return null;

                                                return (
                                                  <div key={itemIndex}>
                                                    {/* Only show ingredientIndexTitle when filteredIngredients exist */}
                                                    {ingredientIndexTitle &&
                                                      filteredIngredients.length > 0 && (
                                                        <div
                                                          className={accordionTabContentHeader()}
                                                        >
                                                          <RichTextA11yWrapper
                                                            field={{
                                                              value: ingredientIndexTitle,
                                                            }}
                                                          />
                                                        </div>
                                                      )}

                                                    <ul
                                                      className={`${ingredientContainer()} ${
                                                        filteredIngredients.length > 1
                                                          ? 'md:columns-3'
                                                          : 'md:columns-1'
                                                      }`}
                                                    >
                                                      {filteredIngredients.map(
                                                        (
                                                          ingredient: IngredientData,
                                                          index: number
                                                        ) => (
                                                          <div
                                                            className={ingredientTitle()}
                                                            key={index}
                                                          >
                                                            {useIngredientLinks &&
                                                            ingredientsPath ? (
                                                              <Link
                                                                className={ingredientLinks()}
                                                                href={`${ingredientsPath}/${ingredient?.name}`}
                                                              >
                                                                <Text
                                                                  field={{
                                                                    value: ingredient?.title?.value,
                                                                  }}
                                                                  tag="li"
                                                                />
                                                              </Link>
                                                            ) : (
                                                              <Text
                                                                field={{
                                                                  value: ingredient?.title?.value,
                                                                }}
                                                                tag="li"
                                                              />
                                                            )}
                                                          </div>
                                                        )
                                                      )}
                                                    </ul>
                                                  </div>
                                                );
                                              }
                                            )}
                                        </div>
                                      </>
                                    );
                                  }
                                )}
                              {/* Disclamier */}
                              {accordion?.fields?.disclaimerText?.value != '' && (
                                <LegalDisclaimer
                                  disclaimerText={accordion?.fields?.disclaimerText}
                                  disclaimerClasses={disclaimerContentClasses()}
                                />
                              )}
                            </div>
                          ) : (
                            <div className={paneBody()}>
                              {usePlaceholder?.value ? (
                                <Placeholder key={index} name={phKey} rendering={props.rendering} />
                              ) : (
                                <>
                                  {/* General Accordion throughout Brands */}
                                  <RichTextA11yWrapper field={topDescription} />
                                  {image?.value?.src ? (
                                    <div className={paneBodyMediaWrapper()}>
                                      <ImageWrapper field={image} />
                                    </div>
                                  ) : (
                                    <>
                                      {video?.value && (
                                        <div className={paneBodyMediaWrapper()}>
                                          <Video class={videoClasses()} responsive field={video} />
                                        </div>
                                      )}
                                    </>
                                  )}
                                  <RichTextA11yWrapper field={bottomDescription} />
                                  {accordion?.fields?.disclaimerText?.value !== '' && (
                                    <LegalDisclaimer
                                      disclaimerText={accordion?.fields?.disclaimerText}
                                      disclaimerClasses={disclaimerContentClasses()}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
            </div>
            {disclaimerText?.value != '' && (
              <LegalDisclaimer
                disclaimerText={disclaimerText}
                disclaimerClasses={disclaimerClasses()}
              />
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Accordion;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const fields = (rendering?.fields?.accordions || []) as CompositeComponents.Accordion.Accordion[];

  const isIngredients = fields?.length > 0 && fields[0]?.fields?.ingredients;

  if (isIngredients && isIngredients?.length > 0) {
    const graphQLClient = graphQLClientFactory({});
    const ingredientService = new IngredientQueryService<RawGqlData>(graphQLClient);
    const results = await ingredientService.fetch(IngredientQuery, {
      datasource: rendering.dataSource,
      language: layoutData?.sitecore?.context?.language,
      site: layoutData?.sitecore?.context?.site?.name,
    });

    const startItemLookup = await graphQLClient.request<RawStartItemGqlData>(startItemQuery, {
      language: layoutData?.sitecore?.context?.language,
      site: layoutData?.sitecore?.context?.site?.name,
    });

    const startItem = startItemLookup?.layout?.item?.id;

    const data = await graphQLClient.request<RawPathGqlData>(wildCardItemQuery, {
      rootPath: startItem, //14866617EA9F43D59855FB352171FF6C
      language: layoutData?.sitecore?.context?.language,
      templates: '96E8B001D2B7425EB15F25D3D8B86076', //Ingredient Detail Page
      itemName: '*',
    });

    const ingredientsURL =
      data?.search?.results?.[0]?.parent?.url?.path?.replace(/^\/[a-z]{2}-[a-z]{2}/, '') || '';

    return {
      staticProps: results,
      ingredientsURL: ingredientsURL,
    };
  } else {
    return {
      staticProps: [],
      ingredientsURL: '',
    };
  }
};

const startItemQuery = /* GraphQL */ `
  query StartItemQuery($language: String!, $site: String!) {
    layout(language: $language, site: $site, routePath: "/") {
      item {
        id
        path
      }
    }
  }
`;

const wildCardItemQuery = /* GraphQL */ `
  query WildCardItemQuery(
    $rootPath: String!
    $language: String!
    $templates: String!
    $itemName: String!
  ) {
    search(
      where: {
        AND: [
          { name: "_language", value: $language, operator: EQ }
          { name: "_path", value: $rootPath }
          { name: "_templates", value: $templates, operator: EQ }
          { name: "_name", value: $itemName, operator: EQ }
        ]
      }
    ) {
      results {
        parent {
          url {
            path
          }
        }
      }
    }
  }
`;
